import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  agentNotificationEventQuery,
  agentNotificationsListQuery,
  agentNotificationsShellQuery,
  saveAgentNotifications,
} from '../../api/settingsEaseedeskAgentNotifications'
import { toast } from '../../ui/notify'
import { LoadFailed } from '../../ui/LoadFailed'
import { Spinner } from '../../ui/Spinner'
import { SelectPicker } from '../../ui/SelectPicker'

/**
 * Settings → Easeedesk → Agent notifications.
 *
 * Element cu element fata de `version-3/easeedesk_settings/notifications/
 * agent_notifications/index.php` + `list.php` + `edit.php`, si gest cu gest
 * fata de `list-1.js` si `edit-1.js`: tabelul de evenimente, `+` care deschide
 * un rand de amanunte sub el, si in el casuta cu tipurile de tichet plus
 * butonul Update.
 *
 * UN SINGUR RAND DESCHIS ODATA, ca in `expandDetails()`: cand se apasa pe alt
 * eveniment, cel dinainte se inchide.
 *
 * CASUTA DE TIPURI e iar `selectpicker`-ul din portal. A stat pe casuta
 * browserului cat `SelectPicker` nu stia decat o singura alegere; stie
 * `multiple` de cand l-a cerut Oncall. Aceleasi atribute ca in `edit.php`:
 * cautare si `data-selected-text-format="count > 0"`, cu titlul "Select..".
 * `name` ramane `ticket_types[]`, deci salvarea pleaca neschimbata.
 */
export function AgentNotificationsPage() {
  const shell = useQuery(agentNotificationsShellQuery())
  const list = useQuery(agentNotificationsListQuery())
  const [open, setOpen] = useState(0)

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="The agent notifications" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const events = list.data?.events ?? []

  return (
    <div>
      <h4>
        <strong>Notifications</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage agent <strong>notifications</strong>
      </label>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed what="The events" error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover ticket-events">
            <thead>
              <tr>
                <th className="a-left"></th>
                <th className="a-left">Ticket Event</th>
                <th className="a-left"></th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <ScreenRow key={event.ID} open={open === event.ID} onToggle={() => setOpen(open === event.ID ? 0 : event.ID)} event={event} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

/** Un rand din tabel plus, cand e deschis, randul de amanunte de sub el. */
function ScreenRow({
  event,
  open,
  onToggle,
}: {
  event: { ID: number; event: string; description: string }
  open: boolean
  onToggle(): void
}) {
  return (
    <>
      <tr className={open ? 'details' : undefined}>
        <td style={{ padding: '2px 6px', width: 50 }}>
          <i
            className={`fas ${open ? 'fa-minus-circle' : 'fa-plus-circle'} show-notifications`}
            aria-hidden="true"
            style={{ cursor: 'pointer' }}
            onClick={onToggle}
          ></i>
        </td>
        <td style={{ padding: '2px 6px' }}>
          {event.event.charAt(0).toUpperCase() + event.event.slice(1)}{' '}
          {event.description !== '' && (
            <i
              className="fas fa-info-circle color-blue font-12"
              style={{ cursor: 'default' }}
              data-toggle="tooltip"
              data-placement="top"
              title={event.description}
            >
              {' '}
            </i>
          )}
        </td>
        <td style={{ padding: '4px 6px', width: 50 }}></td>
      </tr>
      {open && (
        <tr className="row-details">
          <td style={{ backgroundColor: '#FFF' }} colSpan={3}>
            <EventSettings event={event.ID} />
          </td>
        </tr>
      )}
    </>
  )
}

/** `agent_event_notifications` + `edit_agent_notifications`, intr-un loc. */
export function EventSettings({ event }: { event: number }) {
  const detail = useQuery(agentNotificationEventQuery(event))
  const [chosen, setChosen] = useState<number[]>([])

  useEffect(() => {
    if (!detail.data || detail.data.event_id !== event) return
    setChosen(detail.data.chosen)
  }, [detail.data, event])

  const save = useMutation({
    mutationFn: () => saveAgentNotifications(event, chosen),
    onSuccess: () => toast('Event changed.', 'Success'),
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (detail.error) return <LoadFailed what="The rules" error={detail.error} onRetry={() => void detail.refetch()} />
  if (!detail.data) return <Spinner />

  return (
    <>
      <form className="form-horizontal" id="eventSettingsForm" method="post" onSubmit={(e) => e.preventDefault()}>
        <input type="hidden" name="event_id" value={event} readOnly />
        <div className="form-group">
          <div className="col-md-3">
            <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
              Ticket types
            </h5>
          </div>
          <div className="col-md-6">
            <SelectPicker
              id={`agentNotificationTypes-${event}`}
              className="form-control"
              name="ticket_types[]"
              title="Select.."
              multiple
              selectedTextFormat="count > 0"
              options={detail.data.ticket_types.map((type) => ({ value: String(type.ID), label: type.type_name }))}
              value={chosen.map(String)}
              onChange={(next) => setChosen(next.map(Number))}
            />
          </div>
          <div className="col-md-3"></div>
        </div>
      </form>

      <div className="col-md-12">
        <button
          type="button"
          className="btn btn-primary pull-right edit-agent-settings"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          Update
        </button>
      </div>
    </>
  )
}
